/**
 * CompanyMgmtControlHub — iter-217c
 *
 * Intermediate hub under
 *   لوحة التحكم ▸ التحكم والمعلومات للمنصات ▸ CLOUDGM FZ LLC (للتحكم)
 *   ▸ التحكم بإدارة الشركة.
 *
 * Currently exposes ONE sub-card:
 *   • شهادات لمن يهمه الأمر والتهنئة  → CertificateSubjectsControl
 * More sub-cards can be added here without touching the parent screen.
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, Users, Stamp, ChevronLeft, ChevronRight } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

export default function CompanyMgmtControlHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  const CARDS = [
    {
      id: 'certificates',
      titleAr: 'شهادات لمن يهمه الأمر والتهنئة',
      titleEn: 'To-Whom-It-May-Concern & Congratulations',
      descAr:  'إدارة مواضيع الشهادات لكل فئة (تجار، عملاء، شركة، موظفين)',
      descEn:  'Manage certificate subjects per category (merchants, customers, company, employees)',
      icon:  Award,
      color: '#F59E0B',
      route: '/control-panel-settings/company-mgmt/certificates',
    },
    // iter-218r (Feb 2026) — New hub grouping the two company-identity
    // settings (stamp + name). Name was previously buried inside the
    // certificate-subjects "company" tab; stamp was inside Employees Settings.
    // Grouping them under a dedicated hub makes them easy to find.
    {
      id: 'branding',
      titleAr: 'ختم ومسمى الشركة',
      titleEn: 'Company Stamp & Name',
      descAr:  'الاسم الرسمي بالعربي والإنجليزي + الختم الذي يُختم به على الشهادات',
      descEn:  'Official AR/EN name + the stamp used to seal certificates',
      icon:  Stamp,
      color: '#3B9EDC',
      route: '/control-panel-settings/company-mgmt/branding',
    },
    // iter-218 (Feb 2026) — moved from Employee Dashboard.
    // iter-218r — Now hosts SIGNATURES ONLY (stamp moved to /branding).
    {
      id: 'employees-settings',
      titleAr: 'إعدادات الموظفين',
      titleEn: 'Employees Settings',
      descAr:  'إدارة توقيع كل موظف — يُستخدم عند اعتماد الشهادات',
      descEn:  'Manage per-employee signatures — used when approving certificates',
      icon:  Users,
      color: '#8B5CF6',
      route: '/control-panel-settings/company-mgmt/employees',
    },
  ];

  return (
    <DashboardLayout
      title="التحكم بإدارة الشركة"
      titleEn="Company Management Control"
      subtitle="التحكم بإعدادات إدارة الشركة والشهادات"
      subtitleEn="Manage company administration and certificates settings"
      screenCode="FZLC-CTRL"
      backPath="/control-panel-settings"
      onBack={() => navigate('/control-panel-settings')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'التحكم بإدارة الشركة', labelEn: 'Company Management Control' },
      ]}
    >
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="company-mgmt-control-hub">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {visiblePages(CARDS, 'route').map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate(c.route)}
              className="group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border transition-all text-start"
              style={{ borderColor: `${c.color}30` }}
              data-testid={`company-mgmt-ctrl-${c.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 shrink-0"
                     style={{ backgroundColor: `${c.color}15`, borderColor: `${c.color}40` }}>
                  <c.icon className="h-7 w-7" style={{ color: c.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                    {isAr ? c.titleAr : c.titleEn}
                    <Arrow className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: c.color }} />
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {isAr ? c.descAr : c.descEn}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}
